import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { employeeService } from '../../services/employeeService';
import assignmentService from '../../services/assignmentService';
import { locationService } from '../../services/locationService';
import { projectService } from '../../services/projectService';
import { format } from 'date-fns';
import AssignmentBillingConfig from './AssignmentBillingConfig';
import ConfirmDialog from '../Common/ConfirmDialog';

const EmployeeEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project'); // Get project code from URL query param

  // Employee data
  const [employee, setEmployee] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [employeeStatus, setEmployeeStatus] = useState('');

  // Assignments
  const [allAssignments, setAllAssignments] = useState([]); // Store all assignments
  const [filteredAssignments, setFilteredAssignments] = useState([]); // Display filtered assignments

  // Reference data
  const [locations, setLocations] = useState([]);
  const [projects, setProjects] = useState([]);

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Assignment dialog
  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentFormData, setAssignmentFormData] = useState({
    project_code: '',
    location_id: '',
    onboarding_date: '',
    offboarding_date: '',
    bill_rate: '',
  });
  const [dialogError, setDialogError] = useState('');

  // Billing config dialog
  const [billingConfigDialog, setBillingConfigDialog] = useState(false);
  const [selectedAssignmentForBilling, setSelectedAssignmentForBilling] = useState(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  useEffect(() => {
    loadEmployee();
    loadLocations();
    loadProjects();
  }, [id]);

  // Filter assignments when project filter or all assignments change
  useEffect(() => {
    if (projectFilter && allAssignments.length > 0) {
      // Filter to show only the assignment for the selected project
      const filtered = allAssignments.filter(
        (assignment) => assignment.project_code === projectFilter
      );
      setFilteredAssignments(filtered);
    } else {
      // Show all assignments if no filter
      setFilteredAssignments(allAssignments);
    }
  }, [projectFilter, allAssignments]);

  const loadEmployee = async () => {
    try {
      setLoading(true);
      const data = await employeeService.getById(id);
      setEmployee(data);
      setEmployeeName(data.name);
      setEmployeeStatus(data.status || 'Inactive');
      setAllAssignments(data.assignments || []); // Store all assignments
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load employee');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await locationService.getActive();
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const handleUpdateEmployee = async () => {
    setError('');
    setSuccess('');

    if (!employeeName.trim()) {
      setError('Employee name is required');
      return;
    }

    try {
      setSaving(true);
      await employeeService.update(id, { name: employeeName });
      setSuccess('Employee updated successfully!');
      await loadEmployee();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAssignmentDialog = (assignment = null) => {
    setDialogError('');
    if (assignment) {
      // Edit mode
      setEditingAssignment(assignment);
      setAssignmentFormData({
        project_code: assignment.project_code,
        location_id: assignment.location_id,
        onboarding_date: assignment.onboarding_date
          ? format(new Date(assignment.onboarding_date), 'yyyy-MM-dd')
          : '',
        offboarding_date: assignment.offboarding_date
          ? format(new Date(assignment.offboarding_date), 'yyyy-MM-dd')
          : '',
        bill_rate: assignment.bill_rate || '',
      });
    } else {
      // Add mode
      setEditingAssignment(null);
      setAssignmentFormData({
        project_code: '',
        location_id: '',
        onboarding_date: '',
        offboarding_date: '',
        bill_rate: '',
      });
    }
    setAssignmentDialog(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialog(false);
    setEditingAssignment(null);
    setAssignmentFormData({
      project_code: '',
      location_id: '',
      onboarding_date: '',
      offboarding_date: '',
      bill_rate: '',
    });
  };

  const handleAssignmentFormChange = (e) => {
    const { name, value } = e.target;
    setAssignmentFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveAssignment = async () => {
    setDialogError('');
    setSuccess('');

    // Validation
    if (!assignmentFormData.project_code || !assignmentFormData.location_id ||
        !assignmentFormData.onboarding_date || assignmentFormData.bill_rate === '') {
      setDialogError('Please fill in all required fields (project, location, onboarding date, and bill rate)');
      return;
    }

    if (assignmentFormData.offboarding_date &&
        assignmentFormData.offboarding_date < assignmentFormData.onboarding_date) {
      setDialogError('Offboarding date must be after onboarding date');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        emp_id: employee.id,
        project_code: assignmentFormData.project_code,
        location_id: assignmentFormData.location_id,
        onboarding_date: assignmentFormData.onboarding_date,
        offboarding_date: assignmentFormData.offboarding_date || null,
        bill_rate: parseFloat(assignmentFormData.bill_rate),
      };

      if (editingAssignment) {
        // Update existing assignment
        await assignmentService.update(editingAssignment.id, payload);
        setSuccess('Assignment updated successfully!');
      } else {
        // Create new assignment
        await assignmentService.create(payload);
        setSuccess('Assignment created successfully!');
      }

      handleCloseAssignmentDialog();
      await loadEmployee();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to save assignment';
      setDialogError(errorMsg);
      // Keep dialog open so user can fix the error
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (assignment) => {
    setAssignmentToDelete(assignment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) return;
    try {
      setSaving(true);
      await assignmentService.delete(assignmentToDelete.id);
      setSuccess('Assignment deleted successfully!');
      await loadEmployee();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete assignment');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
      setAssignmentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const getAssignmentStatus = (assignment) => {
    const now = new Date();
    const onboardingDate = new Date(assignment.onboarding_date);
    const offboardingDate = assignment.offboarding_date
      ? new Date(assignment.offboarding_date)
      : null;

    if (onboardingDate > now) {
      return { label: 'Future', color: 'info' };
    } else if (!offboardingDate || offboardingDate >= now) {
      return { label: 'Active', color: 'success' };
    } else {
      return { label: 'Completed', color: 'default' };
    }
  };

  const handleOpenBillingConfig = (assignment) => {
    setSelectedAssignmentForBilling(assignment);
    setBillingConfigDialog(true);
  };

  const handleCloseBillingConfig = () => {
    setBillingConfigDialog(false);
    setSelectedAssignmentForBilling(null);
  };

  const handleCancel = () => {
    navigate('/employees');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>
          Employee not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Edit Employee
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Employee Information Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Employee Information
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              label="Employee ID"
              value={employee.emp_id}
              disabled
              sx={{ flex: 1 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={employeeStatus}
                color={employeeStatus === 'Active' ? 'success' : 'default'}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              label="Employee Name"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              required
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateEmployee}
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Name'}
            </Button>
          </Box>
        </Paper>

        {/* Assignments Section */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">
                Project Assignments
              </Typography>
              {projectFilter && (
                <Chip
                  label={`Filtered: ${projectFilter}`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAssignmentDialog()}
              disabled={saving}
            >
              Add Assignment
            </Button>
          </Box>

          {filteredAssignments.length === 0 ? (
            <Alert severity="info">
              {projectFilter
                ? `No assignment found for project ${projectFilter}. This employee may be assigned to other projects.`
                : 'No project assignments found. Click "Add Assignment" to create the first assignment with project details, location, dates, and bill rate.'}
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Onboarding Date</TableCell>
                    <TableCell>Offboarding Date</TableCell>
                    <TableCell>Bill Rate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAssignments.map((assignment) => {
                    const status = getAssignmentStatus(assignment);
                    return (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <strong>{assignment.project?.project_code || assignment.project_code}</strong>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {assignment.project?.name || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {assignment.location?.location_name || '-'}
                        </TableCell>
                        <TableCell>
                          {assignment.onboarding_date
                            ? format(new Date(assignment.onboarding_date), 'MMM dd, yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {assignment.offboarding_date
                            ? format(new Date(assignment.offboarding_date), 'MMM dd, yyyy')
                            : 'Ongoing'}
                        </TableCell>
                        <TableCell>
                          ${assignment.effective_bill_rate ?? assignment.bill_rate}/hr
                          {assignment.bill_rate_source === 'override' && (
                            <Chip label="Override" size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenAssignmentDialog(assignment)}
                            size="small"
                            sx={{ mr: 1 }}
                            title="Edit assignment"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="secondary"
                            onClick={() => handleOpenBillingConfig(assignment)}
                            size="small"
                            sx={{ mr: 1 }}
                            title="Billing configuration"
                          >
                            <SettingsIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(assignment)}
                            size="small"
                            title="Delete assignment"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              fullWidth
            >
              Back to Employee List
            </Button>
          </Box>
        </Paper>

        {/* Assignment Dialog */}
        <Dialog
          open={assignmentDialog}
          onClose={handleCloseAssignmentDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingAssignment ? 'Edit Assignment' : 'Add New Assignment'}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                {dialogError}
              </Alert>
            )}
            <Box sx={{ pt: dialogError ? 0 : 2 }}>
              <TextField
                fullWidth
                select
                label="Project *"
                name="project_code"
                value={assignmentFormData.project_code}
                onChange={handleAssignmentFormChange}
                margin="normal"
                required
                disabled={editingAssignment !== null}
                helperText={editingAssignment ? "Project cannot be changed" : "Select the project"}
              >
                {projects.map((project) => (
                  <MenuItem key={project.project_code} value={project.project_code}>
                    {project.project_code} - {project.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                select
                label="Location *"
                name="location_id"
                value={assignmentFormData.location_id}
                onChange={handleAssignmentFormChange}
                margin="normal"
                required
                helperText="Select the work location for this assignment"
              >
                {locations.map((location) => (
                  <MenuItem key={location.location_id} value={location.location_id}>
                    {location.location_name} {location.country ? `(${location.country})` : ''}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Onboarding Date *"
                name="onboarding_date"
                type="date"
                value={assignmentFormData.onboarding_date}
                onChange={handleAssignmentFormChange}
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Start date for this assignment"
              />

              <TextField
                fullWidth
                label="Offboarding Date"
                name="offboarding_date"
                type="date"
                value={assignmentFormData.offboarding_date}
                onChange={handleAssignmentFormChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="End date (leave empty if ongoing)"
              />

              <TextField
                fullWidth
                label="Bill Rate (per hour) *"
                name="bill_rate"
                type="number"
                value={assignmentFormData.bill_rate}
                onChange={handleAssignmentFormChange}
                margin="normal"
                required
                inputProps={{
                  step: "0.01",
                  min: "0"
                }}
                helperText="Hourly billing rate for this assignment"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignmentDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignment}
              variant="contained"
              disabled={saving}
            >
              {saving ? 'Saving...' : (editingAssignment ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Billing Configuration Dialog */}
        <Dialog
          open={billingConfigDialog}
          onClose={handleCloseBillingConfig}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Billing Configuration - {selectedAssignmentForBilling?.project?.name || selectedAssignmentForBilling?.project_code}
          </DialogTitle>
          <DialogContent>
            {selectedAssignmentForBilling && (
              <AssignmentBillingConfig
                assignmentId={selectedAssignmentForBilling.id}
                projectCode={selectedAssignmentForBilling.project_code}
                assignmentBillRate={selectedAssignmentForBilling.bill_rate}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBillingConfig}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Assignment"
          message="Are you sure you want to delete this assignment?"
          details={assignmentToDelete ? [
            { label: 'Project', value: assignmentToDelete.project_code },
            { label: 'Onboarding Date', value: format(new Date(assignmentToDelete.onboarding_date), 'MMM dd, yyyy') },
          ] : []}
        />
      </Box>
    </Container>
  );
};

export default EmployeeEdit;
